import { useEffect } from 'react';

/**
 * Live error clearing (user 2026-09-05): once a submit has flagged fields, correcting a
 * field removes ITS message immediately instead of leaving it stale until the next
 * submit. Strictly REMOVE-ONLY — the fresh validation result is used solely to decide
 * which existing errors no longer apply; it never adds new ones, so nothing nags the
 * investor while they are still typing. New errors continue to appear only on Submit.
 *
 * @param errors    current error map
 * @param setErrors state setter for the error map
 * @param validate  re-runs the SAME validation the form's submit uses
 * @param deps      when to re-check (typically [formData])
 */
export function useLiveErrorClearing(
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  validate: () => Record<string, string>,
  deps: unknown[]
): void {
  useEffect(() => {
    if (Object.keys(errors).length === 0) return;
    let fresh: Record<string, string>;
    try {
      fresh = validate();
    } catch {
      return; // never let a live re-check break the form
    }
    setErrors((prev) => {
      let changed = false;
      const next: Record<string, string> = { ...prev };
      for (const key of Object.keys(prev)) {
        if (!fresh[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
